import { Command } from 'cmdk'
import { Check } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useScenarioCatalog } from '../hooks/useScenarioCatalog'
import type { ScenarioCatalogEntry, ScenarioCatalogStatus } from '../types'

interface Props {
  selected: readonly string[]
  onChange: (next: string[]) => void
  /**
   * Hide scenarios in these statuses. Defaults to none — even disabled
   * scenarios are kept in the picker because historical alerts may
   * still carry their codes and analysts need to filter by them.
   */
  excludeStatuses?: readonly ScenarioCatalogStatus[]
}

/**
 * Multi-select picker over the scenario catalogue. Search hits both
 * code and name; selected items stay clickable to toggle off. Loads
 * via useScenarioCatalog (5-min staleTime) so the popover opens
 * instantly on the second + use.
 */
export function ScenarioMultiSelect({ selected, onChange, excludeStatuses }: Props) {
  const { data: catalog, isLoading, error } = useScenarioCatalog()

  const items: ScenarioCatalogEntry[] = (catalog ?? []).filter(
    (s) => !excludeStatuses?.includes(s.status),
  )
  const selectedSet = new Set(selected)

  const toggle = (code: string) => {
    if (selectedSet.has(code)) {
      onChange(selected.filter((c) => c !== code))
    } else {
      onChange([...selected, code])
    }
  }

  return (
    <Command label="Filter by scenario">
      <Command.Input
        placeholder="Search scenarios…"
        className="h-8 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-fg"
      />
      <Command.List className="max-h-64 overflow-y-auto">
        {isLoading && (
          <div className="px-2 py-2 text-xs text-muted-fg">Loading scenarios…</div>
        )}
        {error && !isLoading && (
          <div className="px-2 py-2 text-xs text-destructive">
            Could not load scenarios.
          </div>
        )}
        {!isLoading && !error && (
          <Command.Empty className="px-2 py-2 text-xs text-muted-fg">
            No scenarios match.
          </Command.Empty>
        )}
        {items.map((s) => {
          const checked = selectedSet.has(s.code)
          return (
            <Command.Item
              key={s.code}
              value={`${s.code} ${s.name}`}
              onSelect={() => toggle(s.code)}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm aria-selected:bg-muted data-[selected=true]:bg-muted"
            >
              <Check
                size={12}
                className={cn('shrink-0', checked ? 'text-primary' : 'text-transparent')}
                aria-hidden
              />
              <span className="flex min-w-0 flex-col">
                <span className="flex items-baseline gap-1.5">
                  <span className="font-mono text-xs text-muted-fg">{s.code}</span>
                  {s.status !== 'enabled' && (
                    <span
                      className={cn(
                        'rounded px-1 py-px text-[0.625rem] uppercase tracking-wide',
                        s.status === 'shadow' && 'bg-warning/10 text-warning',
                        s.status === 'disabled' && 'bg-muted text-muted-fg',
                      )}
                    >
                      {s.status}
                    </span>
                  )}
                </span>
                <span className="truncate text-fg">{s.name}</span>
              </span>
            </Command.Item>
          )
        })}
      </Command.List>
    </Command>
  )
}

